import React,{useState,useEffect,useRef}from'react';
import{NavLink,useNavigate,useLocation}from'react-router-dom';
import{useTranslation}from'react-i18next';
import{useAuth}from'../../hooks/useAuth';

// Derives an i18n key from a route path — '/bed-board' -> 'bed-board', '/settings/branches' ->
// 'settings.branches'. Only a curated subset of these actually exist in i18n/en.json+sw.json
// (see i18n/index.ts) — for every key that doesn't, t()'s 2nd arg (the item's own English
// `label`) is used as-is, so untranslated nav items still render correctly rather than showing
// a raw "nav.settings.branches" key. Extending Swahili coverage to more items later is just
// adding more keys to sw.json, no code change needed here.
const navKey=(to:string)=>`nav.${to.replace(/^\//,'').replace(/\//g,'.')}`;

type NavChild={to:string;label:string};
type NavItem={to:string;icon:string;label:string;module:string|null;children?:NavChild[]};
const NAV:{section:string;items:NavItem[]}[] = [
  {section:'CLINICAL',items:[
    {to:'/dashboard',           icon:'🏠', label:'Dashboard', module:null},
    {to:'/executive-dashboard', icon:'📈', label:'Executive Dashboard', module:'REPORTS'},
    {to:'/patients',            icon:'👤', label:'Patients', module:'REGISTRATION'},
    {to:'/crm',                 icon:'🤝', label:'CRM', module:'REGISTRATION'},
    {to:'/appointments',        icon:'📅', label:'Appointments', module:'OPD'},
    {to:'/opd',                 icon:'🩺', label:'OPD Consultation', module:'OPD'},
    {to:'/doctor/dashboard',    icon:'👨‍⚕️', label:'Doctor Dashboard', module:'OPD'},
    {to:'/doctor-schedule',     icon:'📆', label:'Doctor Schedule', module:'OPD'},
    {to:'/cpoe',                icon:'📋', label:'CPOE / Orders', module:'EMR'},
    {to:'/ipd',                 icon:'🏥', label:'IPD Admissions', module:'IPD'},
    {to:'/bed-board',           icon:'🛏️', label:'Bed Board', module:'IPD'},
    {to:'/nursing',             icon:'💊', label:'Nursing', module:'IPD'},
    {to:'/indents',             icon:'📦', label:'Nursing Indents', module:'INVENTORY'},
    {to:'/emar',                icon:'💉', label:'eMAR', module:'IPD'},
    {to:'/icu',                 icon:'❤️', label:'ICU', module:'IPD'},
    {to:'/emergency',           icon:'🚨', label:'Emergency', module:'EMERGENCY'},
    {to:'/ot',                  icon:'🔬', label:'Operation Theatre', module:'OT'},
    {to:'/ophthalmology',       icon:'👁️', label:'Ophthalmology', module:'OPD'},
    {to:'/maternity',           icon:'🤰', label:'Maternity & Newborn', module:'IPD'},
    {to:'/renal-care',          icon:'🩸', label:'Renal Care (Dialysis)', module:'IPD'},
    {to:'/mental-health',       icon:'🧠', label:'Mental Wellness', module:'OPD'},
    {to:'/palliative-care',     icon:'🕊️', label:'Palliative Care', module:'IPD'},
    {to:'/mortuary',            icon:'⚱️', label:'Mortuary', module:'IPD'},
    {to:'/discharge-summary',   icon:'📋', label:'Discharge Summary', module:'IPD'},
    {to:'/consents',            icon:'✍️', label:'Consents', module:'CONSENT'},
    {to:'/patient-portal',      icon:'🌐', label:'Patient Portal', module:'REGISTRATION'},
  ]},
  {section:'DIAGNOSTICS',items:[
    {to:'/laboratory',            icon:'🧪', label:'Laboratory', module:'LAB'},
    {to:'/lab/parameter-master',  icon:'🔬', label:'Lab Parameters', module:'LAB'},
    {to:'/lab/reference-ranges',  icon:'📊', label:'Reference Ranges', module:'LAB'},
    {to:'/lab/culture-templates', icon:'🦠', label:'Culture Templates', module:'LAB'},
    {to:'/lab/report-master',     icon:'📄', label:'Report Master', module:'LAB'},
    {to:'/radiology',             icon:'📡', label:'Radiology / PACS', module:'RADIOLOGY'},
    {to:'/bloodbank',             icon:'🩸', label:'Blood Bank', module:'BLOOD_BANK'},
    {to:'/pharmacy',              icon:'💊', label:'Pharmacy', module:'PHARMACY'},
    {to:'/terminology',           icon:'📖', label:'Terminology (ICD/SNOMED)', module:null},
  ]},
  {section:'BILLING & FINANCE',items:[
    {to:'/billing',              icon:'🧾', label:'Billing', module:'BILLING'},
    {to:'/front-office-billing', icon:'💳', label:'Front Office Billing', module:'BILLING'},
    {to:'/insurance',            icon:'🛡️', label:'Insurance / TPA', module:'INSURANCE'},
    {to:'/insurance-preauth',    icon:'📝', label:'Insurance Pre-Auth', module:'INSURANCE'},
    {to:'/corporate',            icon:'🏢', label:'Corporate Master', module:'BILLING'},
    {to:'/payers',               icon:'🏦', label:'Payer Management', module:'INSURANCE'},
    {to:'/finance',              icon:'📊', label:'Finance', module:'FINANCE'},
    {to:'/sha',                  icon:'📊', label:'SHA', module:'INSURANCE'},
    {to:'/shr',                  icon:'🗂️', label:'SHR', module:'EMR'},
  ]},
  {section:'OPERATIONS',items:[
    {to:'/inventory',      icon:'📦', label:'Inventory', module:'INVENTORY'},
    {to:'/vendors',        icon:'🚚', label:'Procurement', module:'INVENTORY'},
    {to:'/diet',           icon:'🥗', label:'Diet & Kitchen', module:'IPD'},
    {to:'/biomedical',     icon:'⚙️', label:'Biomedical', module:'BIOMEDICAL'},
    {to:'/cssd',           icon:'🧰', label:'CSSD', module:'OT'},
    {to:'/communications', icon:'💬', label:'Communications', module:null},
  ]},
  {section:'HR & ADMIN',items:[
    {to:'/hrms',      icon:'👥', label:'HRMS', module:'HR'},
    {to:'/roster',    icon:'📆', label:'Duty Roster', module:'HR'},
    {to:'/workflow',  icon:'🔄', label:'Workflow', module:'ADMIN'},
    {to:'/approvals', icon:'✅', label:'Approvals', module:null},
    {to:'/dms',       icon:'📁', label:'Documents', module:'ADMIN'},
  ]},
  {section:'QUALITY & COMPLIANCE',items:[
    {to:'/quality',   icon:'📈', label:'Quality Indicators', module:'REPORTS'},
    {to:'/moh', icon:'📋', label:'MOH Registers', module:'REPORTS', children:[
      {to:'/moh505', label:'MOH 505 · DSR Weekly'},
      {to:'/moh/333', label:'MOH 333 · Maternity'},
      {to:'/moh/405', label:'MOH 405 · ANC'},
      {to:'/moh/406', label:'MOH 406 · PNC'},
      {to:'/moh/407', label:'MOH 407 · Nutrition'},
      {to:'/moh/409', label:'MOH 409 · Commodity DAR'},
      {to:'/moh/704', label:'MOH 704 · CWC Tally'},
      {to:'/moh/705a', label:'MOH 705A · Under 5'},
      {to:'/moh/705b', label:'MOH 705B · Over 5'},
      {to:'/moh/706', label:'MOH 706 · Lab Summary'},
    ]},
  ]},
  {section:'DIGITAL HEALTH',items:[
    {to:'/fhir',         icon:'⚕️', label:'FHIR R4', module:'REPORTS'},
    {to:'/telemedicine', icon:'📹', label:'Telemedicine', module:'OPD'},
  ]},
  {section:'MASTERS & CONFIG',items:[
    {to:'/settings/branches', icon:'🏥', label:'Branch Management', module:'ADMIN'},
    {to:'/masters',           icon:'🗂️', label:'System Masters', module:'ADMIN'},
    {to:'/location-master',   icon:'🗺️', label:'Location Master', module:'ADMIN'},
    {to:'/charge-master',     icon:'💰', label:'Charge Master', module:'BILLING'},
  ]},
  {section:'ADMINISTRATION',items:[
    {to:'/settings',       icon:'⚙️', label:'Settings & Users', module:'ADMIN'},
    {to:'/audit',          icon:'🔍', label:'Audit Logs', module:'ADMIN'},
    {to:'/audit-log',      icon:'📜', label:'Audit Log Detail', module:'ADMIN'},
    {to:'/sessions',       icon:'💻', label:'Active Sessions', module:null},
    {to:'/system-health',  icon:'💚', label:'System Health', module:'ADMIN'},
    {to:'/backup',         icon:'🗄️', label:'Backup & Restore', module:'ADMIN'},
    {to:'/reports',        icon:'📊', label:'Reports', module:'REPORTS'},
    {to:'/analytics',      icon:'📉', label:'Analytics', module:'REPORTS'},
    {to:'/notifications',  icon:'🔔', label:'Notifications', module:null},
  ]},
];

export default function Sidebar(){
  const {t}=useTranslation();
  const nav=useNavigate();
  const {logout:authLogout,user}=useAuth();
  const location=useLocation();
  const navRef=useRef<HTMLElement>(null);
  const [collapsed,setCollapsed]=useState(false);
  const [expandedSections,setExpandedSections]=useState<Record<string,boolean>>(
    Object.fromEntries(NAV.map(s=>[s.section,true]))
  );
  const [openGroups,setOpenGroups]=useState<Record<string,boolean>>({'/moh':true});

  const canAccess=(module:string|null)=>{
    if(module===null) return true;
    const mods=user?.accessibleModules;
    if(!mods) return false;
    if(mods==='*') return true;
    return (mods as string[]).includes(module);
  };
  const visibleNav=NAV.map(s=>({...s,items:s.items.filter(i=>canAccess(i.module))})).filter(s=>s.items.length>0);

  const pathHits=(to:string)=>location.pathname===to||location.pathname.startsWith(to+'/');
  const itemHits=(item:NavItem)=>pathHits(item.to)||!!item.children?.some(c=>pathHits(c.to));
  const activeChild=visibleNav.flatMap(s=>s.items).flatMap(item=>item.children||[]).find(c=>pathHits(c.to));
  const activeParent=visibleNav.flatMap(s=>s.items).find(itemHits);
  const activeItem=activeChild
    ? {to:activeChild.to, icon:activeParent?.icon||'📋', label:activeChild.label, module:activeParent?.module||null}
    : activeParent;
  const activeSection=visibleNav.find(s=>s.items.some(itemHits))?.section||'';

  // Auto-expand section of active page
  useEffect(()=>{
    if(activeSection)setExpandedSections(p=>({...p,[activeSection]:true}));
    if(activeParent?.children)setOpenGroups(p=>({...p,[activeParent.to]:true}));
  },[activeSection,activeParent?.to]);

  // Auto-scroll active item into view
  useEffect(()=>{
    if(navRef.current){
      const active=navRef.current.querySelector('[aria-current="page"]');
      if(active)active.scrollIntoView({behavior:'smooth',block:'nearest'});
    }
  },[location.pathname]);

  const branch=localStorage.getItem('branchName')||'Clinqov Meditrack';
  const station=localStorage.getItem('stationName')||'Reception';
  const userName=localStorage.getItem('userName')||'User';
  const userRole=localStorage.getItem('userRole')||'Staff';

  const logout=()=>{authLogout();};
  const toggleSection=(section:string)=>setExpandedSections(p=>({...p,[section]:!p[section]}));

  return(
    <div className={`flex flex-col h-screen ${collapsed?'w-16':'w-64'} transition-all duration-300 flex-shrink-0`}
      style={{background:'linear-gradient(180deg, #02357A 0%, #01285C 100%)'}}>

      {/* Logo */}
      <div className="flex items-center justify-between px-3 py-4 border-b border-white/10">
        {!collapsed&&(
          <div className="flex items-center gap-2.5 min-w-0">
            <img src="/logo.png" alt="Clinqov" className="w-9 h-9 rounded-lg object-contain bg-white p-0.5 flex-shrink-0"/>
            <div className="min-w-0">
              <p className="text-white font-bold text-sm leading-tight truncate">Clinqov Meditrack</p>
              <p className="text-white/50 text-xs truncate">Private Limited</p>
            </div>
          </div>
        )}
        {collapsed&&<img src="/logo.png" alt="CM" className="w-9 h-9 rounded-lg object-contain bg-white p-0.5 mx-auto"/>}
        <button onClick={()=>setCollapsed(c=>!c)}
          className="text-white/60 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors flex-shrink-0">
          {collapsed?'→':'←'}
        </button>
      </div>

      {/* User info */}
      {!collapsed&&(
        <div className="px-3 py-2.5 border-b border-white/10">
          <div className="flex items-center gap-2 bg-white/10 rounded-lg px-2.5 py-2">
            <div className="w-7 h-7 rounded-full bg-[#039089] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
              {userName[0]?.toUpperCase()||'U'}
            </div>
            <div className="min-w-0">
              <p className="text-white text-xs font-semibold truncate">{userName}</p>
              <p className="text-white/50 text-xs truncate">{userRole}</p>
            </div>
          </div>
          <div className="mt-1.5 flex items-center gap-1.5 px-1">
            <span className="w-1.5 h-1.5 rounded-full bg-[#039089] flex-shrink-0"/>
            <span className="text-white/60 text-xs truncate">{branch} · {station}</span>
          </div>
        </div>
      )}

      {/* Current page indicator */}
      {!collapsed&&activeItem&&(
        <div className="mx-3 mt-2 mb-1 px-2.5 py-1.5 rounded-lg border border-white/20 bg-white/5">
          <p className="text-white/40 text-[9px] font-bold uppercase tracking-widest">{activeSection}</p>
          <p className="text-white text-xs font-semibold truncate mt-0.5">{activeItem.icon} {t(navKey(activeItem.to),activeItem.label)}</p>
        </div>
      )}

      {/* Nav */}
      <nav ref={navRef} className="flex-1 overflow-y-auto py-2 scrollbar-thin">
        {visibleNav.map(({section,items})=>(
          <div key={section}>
            {!collapsed&&(
              <button onClick={()=>toggleSection(section)}
                className="w-full flex items-center justify-between px-3 py-1.5 text-white/40 hover:text-white/60 transition-colors">
                <span className="text-[10px] font-bold tracking-widest uppercase">{section}</span>
                <span className="text-xs">{expandedSections[section]?'▾':'▸'}</span>
              </button>
            )}
            {(collapsed||expandedSections[section])&&items.map((item)=>{
              const {to,icon,label,children}=item;
              const groupOpen=!!openGroups[to];
              const childActive=!!children?.some(c=>pathHits(c.to));
              return (
                <div key={to}>
                  {children&&!collapsed ? (
                    <div className={`mx-2 flex items-center rounded-lg text-sm ${
                      childActive||location.pathname===to ? 'bg-white/10 text-white' : 'text-white/70'
                    }`}>
                      <NavLink to={to} className="flex items-center gap-3 flex-1 min-w-0 px-2 py-2 hover:text-white">
                        <span className="text-base flex-shrink-0">{icon}</span>
                        <span className="truncate text-sm font-semibold">{t(navKey(to),label)}</span>
                      </NavLink>
                      <button type="button" aria-label="Toggle registers" onClick={()=>setOpenGroups(p=>({...p,[to]:!p[to]}))}
                        className="px-2 py-2 text-[10px] text-white/50 hover:text-white">{groupOpen?'▾':'▸'}</button>
                    </div>
                  ) : (
                    <NavLink to={children?children[0].to:to}
                      aria-current={pathHits(to)||childActive?'page':undefined}
                      className={()=>
                        `flex items-center gap-3 mx-2 px-2 py-2 rounded-lg text-sm transition-all ${
                          pathHits(to)||childActive
                            ?'bg-[#039089] text-white font-semibold shadow-sm'
                            :'text-white/70 hover:bg-white/10 hover:text-white'
                        } ${collapsed?'justify-center':''}`}>
                      <span className="text-base flex-shrink-0">{icon}</span>
                      {!collapsed&&<span className="truncate text-sm">{t(navKey(to),label)}</span>}
                    </NavLink>
                  )}
                  {!collapsed&&children&&groupOpen&&children.map(child=>(
                    <NavLink key={child.to} to={child.to}
                      aria-current={pathHits(child.to)?'page':undefined}
                      className={()=>
                        `flex items-center mx-2 ml-6 px-2 py-1.5 rounded-lg text-xs transition-all ${
                          pathHits(child.to)
                            ?'bg-[#039089] text-white font-semibold'
                            :'text-white/65 hover:bg-white/10 hover:text-white'
                        }`}>
                      <span className="truncate">{child.label}</span>
                    </NavLink>
                  ))}
                </div>
              );
            })}
            {!collapsed&&<div className="h-px bg-white/5 mx-3 my-1"/>}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="border-t border-white/10 p-3">
        {!collapsed&&(
          <button onClick={()=>nav('/change-password')}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-white/60 hover:bg-white/10 hover:text-white transition-colors text-sm">
            <span>🔑</span><span>{t('sidebar.changePassword','Change Password')}</span>
          </button>
        )}
        {collapsed&&(
          <button onClick={()=>nav('/change-password')} className="w-full flex justify-center py-2 text-white/60 hover:text-white" title={t('sidebar.changePassword','Change Password')}>🔑</button>
        )}
        {!collapsed&&(
          <button onClick={()=>nav('/sessions')}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-white/60 hover:bg-white/10 hover:text-white transition-colors text-sm">
            <span>💻</span><span>{t('sidebar.activeSessions','Active Sessions')}</span>
          </button>
        )}
        {collapsed&&(
          <button onClick={()=>nav('/sessions')} className="w-full flex justify-center py-2 text-white/60 hover:text-white" title={t('sidebar.activeSessions','Active Sessions')}>💻</button>
        )}
        {!collapsed&&(
          <button onClick={logout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-white/60 hover:bg-red-500/20 hover:text-red-300 transition-colors text-sm">
            <span>🚪</span><span>{t('sidebar.signOut','Sign Out')}</span>
          </button>
        )}
        {collapsed&&(
          <button onClick={logout} className="w-full flex justify-center py-2 text-white/60 hover:text-red-300" title="Sign Out">🚪</button>
        )}
        {!collapsed&&<p className="text-white/20 text-xs text-center mt-2">Clinqov Meditrack v2.0</p>}
      </div>
    </div>
  );
}
